import React, { useState } from "react";
import { Save, Flame, Beef, Wheat, Droplets } from "lucide-react";
import { useTranslation } from "react-i18next";
import apiService from "../services/api";
import telegramService from "../utils/telegram";
import { useApi } from "../hooks/useApi";
import { useToast } from "./ui/Toast";
import Header from "./ui/Header";
import Card from "./ui/Card";
import Button from "./ui/Button";
import { Input } from "./ui/Input";
import ShimmerSkeleton from "./ui/ShimmerSkeleton";
import ErrorState from "./ui/ErrorState";

interface EditMealProps {
  mealId: string;
  onSave: () => void;
  onCancel: () => void;
}

interface MealForm {
  name: string;
  calories: number;
  protein: number;
  carbs: number;
  fats: number;
}

const MACRO_FIELDS = [
  { name: "calories", icon: Flame, color: "text-brand-text", suffix: "kkal" },
  { name: "protein", icon: Beef, color: "text-info", suffix: "g" },
  { name: "carbs", icon: Wheat, color: "text-warning", suffix: "g" },
  { name: "fats", icon: Droplets, color: "text-danger", suffix: "g" },
] as const;

const EditMeal: React.FC<EditMealProps> = ({ mealId, onSave, onCancel }) => {
  const { t } = useTranslation();
  const toast = useToast();
  const [formData, setFormData] = useState<MealForm | null>(null);
  const [saving, setSaving] = useState(false);

  const { loading, error, refetch } = useApi(async () => {
    const meal = await apiService.getMealById(mealId);
    setFormData({
      name: meal.name,
      calories: meal.calories,
      protein: meal.protein,
      carbs: meal.carbs,
      fats: meal.fats,
    });
    return meal;
  }, [mealId]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) =>
      prev ? { ...prev, [name]: name === "name" ? value : Number(value) } : prev,
    );
  };

  const handleSave = async () => {
    if (!formData) return;
    setSaving(true);
    try {
      await apiService.updateMeal(mealId, formData);
      telegramService.haptic("success");
      toast.show(t("editMeal.updated"), "success");
      onSave();
    } catch (err) {
      console.error("Error updating meal:", err);
      telegramService.haptic("error");
      toast.show(t("editMeal.updateFailed"), "error");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <ShimmerSkeleton variant="mealList" />;
  if (error || !formData) {
    return (
      <>
        <Header title={t("editMeal.title")} onBack={onCancel} />
        <ErrorState title={t("editMeal.loadError")} onRetry={refetch} />
      </>
    );
  }

  return (
    <div className="pb-8">
      <Header title={t("editMeal.title")} onBack={onCancel} />

      <div className="px-4 space-y-5">
        <Card padding="lg" className="space-y-4">
          <Input
            label={t("editMeal.foodName")}
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
          />

          <div className="grid grid-cols-2 gap-3">
            {MACRO_FIELDS.map((field) => (
              <div key={field.name}>
                <span className="flex items-center gap-1.5 text-xs font-bold text-content-secondary mb-1.5">
                  <field.icon size={13} className={field.color} />
                  {t(`editMeal.${field.name}`)}
                </span>
                <Input
                  type="number"
                  inputMode="numeric"
                  name={field.name}
                  min={0}
                  value={formData[field.name]}
                  onChange={handleChange}
                  suffix={field.suffix}
                />
              </div>
            ))}
          </div>
        </Card>

        <Button
          variant="primary"
          size="lg"
          fullWidth
          icon={Save}
          loading={saving}
          onClick={handleSave}
        >
          {t("editMeal.saveChanges")}
        </Button>
      </div>
    </div>
  );
};

export default EditMeal;
